import { AxiosError } from 'axios';
import { eAccessControlLogoutReason } from 'ui-toolkit/enums/eAccessControlLogoutReason';
import { Logger } from '../logging/LoggerService';
import { clientUrls } from './ClientUrlFactory';
import { getDefaultJWTCookie } from '../../components/accessControl/getSpruceJWTCookie';

// FYI: This catches Axios errors so that they are logged. It also tries to unpack well known error schema
// This could be written as a very "cool" set of ternary operations, but `if` statements are clearer to read
export const axiosErrorHandler = (e: AxiosError) => {
  axiosErrorHandlerExpects([])(e);
  throw e;
};

export const axiosErrorHandlerExpects = (normalErrors: number[]) => (e: AxiosError<any, any>) => {
  if (!normalErrors.includes(e.response?.status || 0)) {
    Logger.error(`API Request`, e);
  }
  const statuses = [400, 401, ...normalErrors];
  if (e.isAxiosError) {
    if (e.response?.status === 401 && !getDefaultJWTCookie()) {
      //FYI: This assumes a 401 without a cookie means the session is expired.
      window.location.href = clientUrls.urlFor.logoutAuth0(
        eAccessControlLogoutReason.EXPIRED,
        window.location.pathname
      );
      throw new Error('Your Session Has Expired. Redirecting you to the logout page');
    }
    if (!!e.response && statuses.includes(e.response.status) && e.response?.data) {
      switch (true) {
        case typeof e.response.data.error === 'string':
          throw e.response.data.error;
        case typeof e.response.data === 'string':
          throw e.response.data;
        case typeof e.response.data.message === 'string':
          throw e.response.data.message;
      }
    }
  }
  throw e;
};
// FYI: In some cases, Timber returns 400 responses that are standard or conventional to Spring validation handling.
// Previously, Timber would return a 409 for an invalid request param. (e.g. an email for an Admin ending in @gmail.com)
// To anticipate this, we unpack those 400 responses here.
// Otherwise, this mimics axiosErrorHandler
export const axiosSpringValidationErrorHandler = (e: AxiosError<any, any>) => {
  Logger.error('API Request', e);
  if (e.isAxiosError && !!e.response) {
    if ([400, 401].includes(e.response.status) && !!e.response.data) {
      if (!!e.response.data.errors) {
        const errors: ISpringValidationResponseErrors[] = e.response.data.errors;
        if (errors.length > 0) {
          throw new Error(errors[0].code);
        }
      } else if (typeof e.response.data.error === 'string') {
        throw new Error(e.response.data.error);
      } else if (typeof e.response.data === 'string') {
        throw new Error(e.response.data);
      }
    }
  }
  throw e;
};

interface ISpringValidationArgument {
  codes: string[];
  arguments: unknown;
  defaultMessage: string;
  code: string;
}

interface ISpringValidationResponseErrors {
  codes: string[];
  defaultMessage: string;
  objectName: string;
  field: string;
  rejectedValue: string;
  bindingFailure: boolean;
  code: string;
  arguments: ISpringValidationArgument[];
}
